<template>
  <q-layout view="lHr Lpr lFf">
    <q-page-container>
      <router-view />
    </q-page-container>
  </q-layout>
</template>

<script setup>
import { computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useMeta } from 'quasar'
import { useSpaUpdate } from '@/composables/useSpaUpdate'
import { useAuthStore } from '@/stores/auth'
import { useBusinessStore } from '@/stores/business'
import { NAV_LINKS } from '@/config/navLinks'

// Initialize SPA auto-update checking for browser users.
// This will periodically check a small version file and prompt
// users to reload when a new version is deployed.
useSpaUpdate()

const appName = 'Blu'
const route = useRoute()
const authStore = useAuthStore()
const businessStore = useBusinessStore()
const sortedNavLinks = [...NAV_LINKS].sort((a, b) => b.link.length - a.link.length)

const toTitleCase = (value) => {
  if (!value) return ''
  const withoutQuery = value.split('?')[0]
  const withoutLeading = withoutQuery.replace(/^\/+/, '')
  if (!withoutLeading) return ''
  const lastSegment = withoutLeading.split('/').filter(Boolean).slice(-1)[0] || withoutLeading
  const spaced = lastSegment
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .trim()
  if (!spaced) return ''
  return spaced.split(' ').filter(Boolean).map(word => `${word[0].toUpperCase()}${word.slice(1)}`).join(' ')
}

const getNavTitle = (path) => {
  if (!path) return ''
  const match = sortedNavLinks.find((link) => path === link.link || path.startsWith(`${link.link}/`))
  return match?.title || ''
}

const pageTitle = computed(() => {
  const metaTitle = route.meta?.title
  if (metaTitle) return metaTitle
  const navTitle = getNavTitle(route.path)
  if (navTitle) return navTitle
  const routeName = typeof route.name === 'string' ? route.name : ''
  return toTitleCase(routeName || route.path || '')
})

const businessName = computed(() => businessStore.info?.name || '')
const fullTitle = computed(() => [appName, pageTitle.value, businessName.value].filter(Boolean).join(' - '))

useMeta(() => ({
  title: fullTitle.value
}))

watch(
  () => authStore.isAuthenticated,
  async (isAuthenticated) => {
    if (isAuthenticated) {
      if (!businessStore.info) {
        try {
          await businessStore.fetchInfo()
        } catch (error) {
          console.warn('Failed to fetch business info for title:', error)
        }
      }
    } else {
      businessStore.info = null
    }
  },
  { immediate: true }
)
</script>
